import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import LanguageToggle from './LanguageToggle';
import { secureStorage } from '../utils/secureStorage';
import { BACKEND_URL } from '../utils/backendUrl';

/**
 * NexusHeader - Unified header with EMZI NEXUS logo
 * Matches login page design with blended logo background (#0e67a2)
 */
const NexusHeader = ({ onLogout }) => {
  const navigate = useNavigate();
  const { user, getRoleName, isSupervisor, isManagement, isAdmin, isExecutor, login } = useAuth();
  const t = useLang();
  const [unreadCount, setUnreadCount] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const isImpersonating = secureStorage.getItem('nexus_impersonating') === 'true';

  const handleReturnToAdmin = () => {
    const adminToken = secureStorage.getItem('nexus_admin_return_token');
    const adminRefresh = secureStorage.getItem('nexus_admin_return_refresh');
    const adminUser = secureStorage.getItem('nexus_admin_return_user');
    
    if (adminToken && adminUser) {
      // Restore admin session
      login({
        access_token: adminToken,
        refresh_token: adminRefresh || undefined,
        user: JSON.parse(adminUser)
      });
      
      // Clean up impersonation data
      secureStorage.removeItem('nexus_admin_return_token');
      secureStorage.removeItem('nexus_admin_return_refresh');
      secureStorage.removeItem('nexus_admin_return_user');
      secureStorage.removeItem('nexus_impersonating');
      
      navigate('/admin/users');
    }
  };

  useEffect(() => {
    loadUnreadCount();
    const interval = setInterval(() => {
      if (isVisible) loadUnreadCount();
    }, 30000);

    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);
      if (!document.hidden) loadUnreadCount();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isVisible]);

  const loadUnreadCount = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/inbox/unread-count`, {
        headers: {
          'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) return;
      const data = await response.json();
      setUnreadCount(data.total || 0);
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  };

  const handleInboxClick = () => {
    navigate('/communications?tab=inbox');
  };

  const getBadgeText = () => {
    if (unreadCount === 0) return null;
    return unreadCount > 9 ? '9+' : unreadCount.toString();
  };

  const badgeText = getBadgeText();

  // Get role display text
  const getRoleDisplay = () => {
    if (isAdmin()) return 'Admin';
    if (isManagement()) return 'Management';
    if (isSupervisor()) return 'Supervisor';
    if (isExecutor()) return 'Executor';
    return getRoleName() || 'User';
  };

  return (
    <>
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div
          data-testid="impersonation-banner"
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 60,
            background: 'linear-gradient(90deg, #7c3aed 0%, #6d28d9 100%)',
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            color: 'white',
            fontSize: '13px',
            fontWeight: '600'
          }}
        >
          <span>
            {t('admin.users.impersonate.viewing') || 'Viewing as'}: {user?.email || 'User'} ({user?.role})
          </span>
          <button
            data-testid="return-to-admin-btn"
            onClick={handleReturnToAdmin}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              border: '2px solid rgba(255, 255, 255, 0.5)',
              padding: '6px 16px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            {t('admin.users.impersonate.returnToAdmin') || 'Return to Admin'}
          </button>
        </div>
      )}
    <header
      className="sticky top-0 z-50 flex-shrink-0"
      style={{
        background: '#002c88',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
        paddingTop: 'env(safe-area-inset-top)'
      }}
    >
      <div className="flex items-center justify-between px-3 py-2">
        {/* Left: Logo & Role Info */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* EMZI NEXUS Logo - Larger with cropped version */}
          <img 
            src="/emzi-nexus-logo-cropped.png" 
            alt="EMZI NEXUS" 
            className="flex-shrink-0"
            style={{ 
              height: '48px',
              width: 'auto',
              objectFit: 'contain',
              maxWidth: '200px'
            }}
          />
          
          {/* Role Badge - Compact */}
          <div className="hidden sm:flex items-center gap-2">
            <span 
              className="px-2 py-1 rounded-lg text-xs font-bold"
              style={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: '#FFFFFF'
              }}
            >
              {getRoleDisplay()}
            </span>
            
            {/* ISO Compliance - Only for Management/Admin */}
            {(isManagement() || isAdmin()) && (
              <span 
                className="px-2 py-0.5 rounded-full text-xs font-semibold"
                style={{ 
                  backgroundColor: '#E9FFF4',
                  color: '#16C784'
                }}
              >
                ISO ✓
              </span>
            )}
          </div>
        </div>

        {/* Right Section - Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Language Toggle */}
          <LanguageToggle placement="bottom-end" />
          
          {/* Inbox Icon */}
          <button
            onClick={handleInboxClick}
            className="relative flex items-center justify-center rounded-xl transition-all hover:bg-white/15 active:bg-white/20"
            style={{ width: '40px', height: '40px', color: '#FF7B00' }}
            aria-label="Open Inbox"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z" fill="currentColor"/>
            </svg>
            {badgeText && (
              <span
                className="absolute top-0.5 right-0.5 min-w-[16px] h-4 rounded-full flex items-center justify-center text-xs font-bold"
                style={{
                  backgroundColor: '#FF4D4F',
                  color: 'white',
                  fontSize: '10px',
                  padding: '0 4px',
                  border: '2px solid #0e67a2'
                }}
              >
                {badgeText}
              </span>
            )}
          </button>

          {/* Sign Out Button */}
          <button
            onClick={onLogout}
            className="flex items-center gap-1.5 rounded-xl font-semibold transition-all px-3 py-2"
            style={{
              backgroundColor: 'transparent',
              color: '#FF7B00',
              fontSize: '13px',
              border: '2px solid #FF7B00'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 123, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            aria-label="Sign Out"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M17 7L15.59 8.41L18.17 11H8V13H18.17L15.59 15.59L17 17L22 12L17 7ZM4 5H12V3H4C2.9 3 2 3.9 2 5V19C2 20.1 2.9 21 4 21H12V19H4V5Z" fill="currentColor"/>
            </svg>
            <span className="hidden sm:inline">Sign Out</span>
          </button>
        </div>
      </div>

      {/* Mobile Role Badge Row - Only show on small screens */}
      <div className="flex sm:hidden items-center justify-between px-3 pb-2 -mt-1">
        <div className="flex items-center gap-2">
          <span 
            className="px-2 py-0.5 rounded-lg text-xs font-bold"
            style={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: '#FFFFFF'
            }}
          >
            {getRoleDisplay()}
          </span>
          
          {(isManagement() || isAdmin()) && (
            <span 
              className="px-2 py-0.5 rounded-full text-xs font-semibold"
              style={{ 
                backgroundColor: '#E9FFF4',
                color: '#16C784'
              }}
            >
              ISO ✓
            </span>
          )}
        </div>
        
        {/* User name on mobile */}
        <span className="text-white/70 text-xs truncate max-w-[150px]">
          {user?.name || user?.email?.split('@')[0]}
        </span>
      </div>
    </header>
    </>
  );
};

export default NexusHeader;
